import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import * as api from "../api";

// Shared by SuperAdminNavbar.js (the persistent "Expires in Xd" chip),
// SubscriptionTab.js (the Extend Subscription panel), and OverviewTab.js
// (the "Subscription" stat card) - all three were independently calling
// api.getSubscription() on their own mount, so saving an extension in
// SubscriptionTab never updated the other two without a full page reload.
// One shared fetch + one shared value fixes that; setSubscription is
// exposed so a save can optimistically update every consumer immediately
// instead of waiting on a second round trip.
const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  // Guards refreshSubscription's setState calls below against firing after
  // this Provider unmounts (only happens on a full logout, but the initial
  // fetch could still be in flight if that happens quickly) - same
  // cancelled-flag idea as UsersTab.js's viewStoreInfo fetch, tracked via a
  // ref since it needs to be read from inside a useCallback, not a single
  // effect's own closure.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshSubscription = useCallback(async () => {
    try {
      const { data } = await api.getSubscription();
      if (mountedRef.current) setSubscription(data?.data || null);
    } catch (error) {
      // Non-fatal - every consumer already handles a null subscription.
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  const value = useMemo(
    () => ({ subscription, setSubscription, loading, refreshSubscription }),
    [subscription, loading, refreshSubscription]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
